import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Phone, Trash2, MapPin, ExternalLink, Bell, BellRing, MessageCircle } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { cardCls, btnGhost } from "./styles";

const STATUSES = ["جديد", "قيد التنفيذ", "مكتمل", "ملغي"];
const statusColor = (s) =>
  s === "جديد" ? "text-yellow-400 border-yellow-400/40" :
  s === "قيد التنفيذ" ? "text-blue-400 border-blue-400/40" :
  s === "مكتمل" ? "text-green-400 border-green-400/40" : "text-zinc-500 border-zinc-600";

const waCustomer = (phone) => `https://wa.me/966${String(phone || "").replace(/\D/g, "").replace(/^0/, "")}`;

export const RequestsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notifOn, setNotifOn] = useState(typeof Notification !== "undefined" && Notification.permission === "granted");
  const knownIds = useRef(null);

  const notifyNew = (r) => {
    toast.success(`طلب جديد: ${r.name} — ${r.service || r.phone}`);
    if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      new Notification("طلب خدمة جديد — نجم الإطارات", { body: `${r.name} — ${r.service || ""} — ${r.phone}` });
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/admin/requests");
      if (knownIds.current !== null) {
        data.filter((r) => !knownIds.current.has(r.id)).forEach(notifyNew);
      }
      knownIds.current = new Set(data.map((r) => r.id));
      setItems(data);
    } catch (e) {
      if (knownIds.current === null) toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const enableNotif = async () => {
    if (typeof Notification === "undefined") return toast.error("المتصفح ما يدعم التنبيهات");
    const p = await Notification.requestPermission();
    setNotifOn(p === "granted");
    if (p === "granted") toast.success("تم تفعيل التنبيهات — اترك اللوحة مفتوحة ويوصلك تنبيه فوري لكل طلب جديد");
    else toast.error("التنبيهات مرفوضة من إعدادات المتصفح");
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/requests/${id}`, { status });
      toast.success("تم تحديث الحالة");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("حذف هذا الطلب نهائيًا؟")) return;
    try {
      await api.delete(`/admin/requests/${id}`);
      toast.success("تم حذف الطلب");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <div className="text-sm text-zinc-500">تحديث تلقائي كل ٣٠ ثانية</div>
        <button data-testid="notif-toggle-btn" onClick={enableNotif} className={`${btnGhost} flex items-center gap-1.5 ${notifOn ? "border-yellow-400/60 text-yellow-400" : ""}`}>
          {notifOn ? <BellRing className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
          {notifOn ? "التنبيهات مفعّلة" : "تفعيل تنبيهات الطلبات"}
        </button>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm py-10 text-center">جارٍ التحميل...</div>
      ) : !items.length ? (
        <div data-testid="requests-empty" className="text-zinc-500 text-sm py-10 text-center">لا توجد طلبات بعد — طلبات العملاء من الموقع تظهر هنا.</div>
      ) : (
        <div className="space-y-4">
          {items.map((r) => (
            <div key={r.id} data-testid={`request-row-${r.id}`} className={cardCls}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-bold text-white">{r.name}</span>
                    <a href={`tel:${r.phone}`} className="flex items-center gap-1 text-yellow-400 text-sm" dir="ltr">
                      <Phone className="w-3.5 h-3.5" /> {r.phone}
                    </a>
                    <a
                      data-testid={`request-wa-${r.id}`}
                      href={waCustomer(r.phone)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-green-400 text-sm border border-green-500/40 rounded-full px-2.5 py-0.5 hover:bg-green-500/10 transition-colors"
                    >
                      <MessageCircle className="w-3.5 h-3.5" /> واتساب العميل
                    </a>
                  </div>
                  <div className="text-xs text-zinc-500 mt-1">{new Date(r.created_at).toLocaleString("ar-SA")}</div>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    data-testid={`request-status-${r.id}`}
                    value={r.status}
                    onChange={(e) => setStatus(r.id, e.target.value)}
                    className={`bg-[#0A0A0A] border rounded-full px-3 py-1.5 text-xs font-bold outline-none ${statusColor(r.status)}`}
                  >
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button data-testid={`request-delete-${r.id}`} onClick={() => del(r.id)} className="text-zinc-500 hover:text-red-400 p-1.5 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2 mt-4 text-sm">
                {r.service && <div><span className="text-zinc-500">الخدمة: </span><span className="text-zinc-200">{r.service}</span></div>}
                {(r.car_brand || r.car_model) && <div><span className="text-zinc-500">السيارة: </span><span className="text-zinc-200">{r.car_brand} {r.car_model}</span></div>}
                {r.tire_size && <div><span className="text-zinc-500">المقاس: </span><span className="text-zinc-200" dir="ltr">{r.tire_size}</span></div>}
                {r.area && <div><span className="text-zinc-500">المنطقة: </span><span className="text-zinc-200">{r.area}</span></div>}
              </div>
              {r.problem && <div className="mt-3 text-sm text-zinc-400 bg-[#0A0A0A] border border-[#28282D] rounded-lg px-3 py-2">{r.problem}</div>}
              {r.location_url && (
                <a href={r.location_url} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-xs text-yellow-400 hover:underline">
                  <MapPin className="w-3.5 h-3.5" /> موقع العميل <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
